import { Box } from "@mui/material";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { Flex } from "Components/_Shared/Core/Flex";
import { ReactComponent as StackIcon } from "Components/_Shared/Icons/Assets/stack.svg";
import { useGetGoalModelingDetail } from "Infrastructure/ApiZfp/AnalyzerApi";
import type { AllocatedAmount, GoalOverview } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { useContext } from "react";
import { GoalLabel } from "../_Shared/Goals/GoalLabel";
import { Tag } from "../_Shared/Tag";
import { GoalFulfillmentItemFundsTooltip } from "./GoalFulfillmentItemFundsTooltip";

type Props = {
  goal: GoalOverview;
};

const getOneTimeFundsData = (allocatedAmount: AllocatedAmount[]): string => {
  if (allocatedAmount.length === 0) return formatCurrency(0, undefined);

  const sumValue = allocatedAmount.reduce((total, item) => {
    return total + (item.amount?.value ?? 0);
  }, 0);

  return formatCurrency(sumValue, allocatedAmount[0].amount?.currencyCode);
};

export const GoalFulfillmentItem = ({ goal }: Props) => {
  const { t } = useResource();
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const { data: goalModeling } = useGetGoalModelingDetail(analysisId, versionId, goal.guid, {
    query: { enabled: !!goal.guid },
  });

  if (!goalModeling) return null;

  return (
    <Box pl={Spacing.L} pt={Spacing.XL}>
      <GoalLabel
        goal={goal}
        extra={
          <Flex gap={Spacing.M} flexWrap={"wrap"}>
            <Tag
              variant="solid"
              color="grey"
              title={`${t(Resources.AnalysisDetail.GoalsFulfillment.RegularFunds)}:`}
              titleSecondary={formatCurrency(
                goalModeling.allocatedAmount?.amount?.value,
                goalModeling.allocatedAmount?.amount?.currencyCode,
              )}
              icon={<StackIcon width={12} height={12} />}
            />
            <Tag
              variant="solid"
              color="grey"
              title={`${t(Resources.AnalysisDetail.GoalsFulfillment.OneTimeFunds)}:`}
              titleSecondary={getOneTimeFundsData(goalModeling.allocatedAssetAmounts)}
              icon={<StackIcon width={12} height={12} />}
              after={<GoalFulfillmentItemFundsTooltip allocatedAssetAmounts={goalModeling.allocatedAssetAmounts} />}
            />
          </Flex>
        }
      />
    </Box>
  );
};
